import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import PhotoModal from './components/PhotoModal';

const HomePage = () => {
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://incendios.justclean.es';

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#organization`,
        name: 'JUSTCLEAN INCENDIOS',
        url: siteUrl,
        telephone: '+34602365996',
        email: 'info@incendios.justclean.es',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Barcelona',
          addressRegion: 'Cataluña',
          addressCountry: 'ES',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: '41.3873974',
          longitude: '2.1687300',
        },
        areaServed: {
          '@type': 'City',
          name: 'Barcelona',
        },
        priceRange: '$$',
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '20:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '09:00',
            closes: '14:00',
          },
        ],
      },
      {
        '@type': 'Service',
        '@id': `${siteUrl}/#service`,
        serviceType: 'Limpieza Post Incendio',
        provider: {
          '@id': `${siteUrl}/#organization`,
        },
        areaServed: {
          '@type': 'City',
          name: 'Barcelona',
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'JUSTCLEAN INCENDIOS',
        publisher: {
          '@id': `${siteUrl}/#organization`,
        },
      },
    ],
  };

  const services = [
    {
      icon: 'ri-brush-line',
      title: 'Eliminación de Hollín y Humo',
      description: 'Limpieza técnica de paredes, techos y suelos con productos especializados según el tipo de material afectado.',
    },
    {
      icon: 'ri-wind-line',
      title: 'Neutralización de Olores',
      description: 'Tratamientos profesionales para eliminar el olor a humo persistente y devolver la habitabilidad al espacio.',
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Retirada de Residuos',
      description: 'Desescombro y retirada de enseres dañados, cenizas y materiales afectados que no puedan recuperarse.',
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Limpieza Profunda de Cocinas',
      description: 'Desengrase y limpieza de campanas, azulejos y superficies con residuos grasos de hollín.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Desinfección y Saneamiento',
      description: 'Protocolos de higienización completa con ventilación controlada para garantizar un ambiente seguro.',
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Preparación para Pintura',
      description: 'Acondicionamiento de superficies y entrega final lista para trabajos de reparación y pintura.',
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Evaluación Inicial',
      description: 'Inspección detallada del alcance de los daños, documentación fotográfica y coordinación con perito/aseguradora.',
    },
    {
      number: '02',
      title: 'Plan de Intervención',
      description: 'Diseño del protocolo de limpieza específico según materiales afectados, extensión y nivel de gravedad.',
    },
    {
      number: '03',
      title: 'Ejecución Profesional',
      description: 'Limpieza técnica con EPIs, protección de zonas comunes y aplicación de tratamientos especializados.',
    },
    {
      number: '04',
      title: 'Verificación Final',
      description: 'Control de calidad, neutralización de olores confirmada y entrega del espacio listo para reparaciones.',
    },
  ];

  return (
    <>
      <SEOHead
        title="Limpieza Post Incendio Barcelona | JUSTCLEAN INCENDIOS"
        description="Especialistas en limpieza después de incendio en Barcelona. Eliminación de hollín, neutralización de olores, desinfección. Intervención rápida. ☎ 602 36 59 96"
        keywords="limpieza post incendio Barcelona, limpieza después de incendio, limpieza de hollín, neutralizar olor a humo, desodorización post incendio"
        canonical="/"
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/cb7b488d35e61170490ffd8e589958bd.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          >
            Limpieza post incendio en Barcelona
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-200 mb-8 max-w-3xl mx-auto"
          >
            <a 
              href="https://www.justclean.es/limpieza-post-incendio/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-[#ff0000] transition-colors underline decoration-[#ff0000]/50 hover:decoration-[#ff0000]"
            >
              Especialistas en limpieza de hollín
            </a>, neutralización de olores y saneamiento tras incendios. Intervención rápida con protocolos profesionales y coordinación con aseguradoras.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <a
              href="tel:+34602365996"
              className="px-8 py-4 bg-[#ff0000] text-white text-base font-semibold rounded-lg hover:bg-[#cc0000] transition-all shadow-lg whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Llamar Ahora
            </a>
            <a
              href="https://wa.me/34602365996"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-[#25D366] text-white text-base font-semibold rounded-lg hover:bg-[#1da851] transition-all shadow-lg whitespace-nowrap"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              WhatsApp
            </a>
            <button
              onClick={() => setIsPhotoModalOpen(true)}
              className="px-8 py-4 bg-white text-gray-900 text-base font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap"
            >
              <i className="ri-camera-line mr-2"></i>
              Enviar Fotos
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12 flex flex-wrap justify-center gap-4 sm:gap-8 text-white px-4"
          >
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-2xl text-[#ff0000]"></i>
              <span className="text-xs sm:text-sm">Intervención Rápida</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-2xl text-[#ff0000]"></i>
              <span className="text-xs sm:text-sm">Protocolos Profesionales</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-file-list-line text-2xl text-[#ff0000]"></i>
              <span className="text-xs sm:text-sm">Asesoría gratuita con su compañía de seguros</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Act Fast */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Recuperamos tu espacio tras el incendio
              </h2>
              <p className="text-base text-gray-700 mb-4">
                Después de un incendio, el tiempo es un factor crítico. El <strong>hollín y el humo</strong> no solo dejan manchas visibles, sino que continúan causando daños incluso después de que las llamas se hayan extinguido.
              </p>
              <p className="text-base text-gray-700 mb-4">
                Las partículas de hollín son ácidas y corrosivas. Si no se eliminan rápidamente, se adhieren a las superficies, oxidan metales, manchan permanentemente paredes y techos, y el <strong>olor a humo se intensifica</strong> con el paso de los días.
              </p>
              <p className="text-base text-gray-700 mb-4">
                Recomendamos iniciar la <strong>limpieza post incendio</strong> lo antes posible, idealmente dentro de las primeras 48-72 horas, siempre que el acceso sea seguro y se cuente con la aprobación del perito o la aseguradora.
              </p>
              <div className="bg-red-50 border-l-4 border-[#ff0000] p-4 mt-6">
                <p className="text-sm text-gray-800">
                  <strong>Importante:</strong> Cuanto más tiempo permanezca el hollín sin tratar, mayor será el daño estructural y más difícil será la recuperación completa del espacio.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=fire%20damaged%20room%20with%20soot%20on%20walls%20before%20cleaning%20professional%20photo%20realistic&width=800&height=900&seq=why001&orientation=portrait"
                alt="Daños por hollín en interior"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-top rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nuestros servicios especializados
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Ofrecemos soluciones integrales para la recuperación de espacios afectados por incendios en Barcelona y provincia.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-2xl text-[#ff0000]`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/servicios"
              className="inline-flex items-center px-6 py-3 bg-[#ff0000] text-white text-sm font-semibold rounded-lg hover:bg-[#cc0000] transition-all whitespace-nowrap"
            >
              Ver Todos los Servicios
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cómo trabajamos
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Un protocolo estructurado en 4 fases para garantizar resultados profesionales y seguros.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-gray-50 p-6 rounded-lg h-full">
                  <div className="text-5xl font-bold text-[#ff0000] opacity-20 mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {step.description}
                  </p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Por qué elegirnos
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Ofrecemos servicios de <strong>limpieza post incendio</strong> en toda la ciudad de Barcelona y el Área Metropolitana, garantizando una <strong>intervención rápida</strong> cuando más lo necesitas.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95777.49789922334!2d2.0787279!3d41.3873974!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a49816718e30e5%3A0x44b0fb3d4f47660a!2sBarcelona!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
                title="Mapa de Barcelona"
              ></iframe>
            </div>

            <div>
              <div className="bg-white p-8 rounded-lg shadow-lg mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#ff0000] rounded-lg flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  Municipios que Cubrimos
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    'Barcelona Ciudad',
                    'L\'Hospitalet de Llobregat',
                    'Badalona',
                    'Sabadell',
                    'Terrassa',
                    'Santa Coloma de Gramenet',
                    'Cornellà de Llobregat',
                    'Sant Boi de Llobregat',
                    'Mataró',
                    'Granollers',
                    'Viladecans',
                    'El Prat de Llobregat',
                    'Rubí',
                    'Cerdanyola del Vallès',
                    'Esplugues de Llobregat',
                    'Gavà',
                  ].map((city, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-red-50 hover:shadow-md transition-all cursor-pointer"
                    >
                      <i className="ri-map-pin-fill text-[#ff0000] text-lg"></i>
                      <span className="text-sm font-medium text-gray-800">{city}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white p-6 rounded-lg border-l-4 border-[#ff0000]">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-[#ff0000]"></i>
                  ¿Tu municipio no aparece?
                </h4>
                <p className="text-sm text-gray-700 mb-4">
                  También ofrecemos servicios en otros municipios de la provincia de Barcelona. Contáctanos para confirmar disponibilidad en tu zona.
                </p>
                <a
                  href="tel:+34602365996"
                  className="inline-flex items-center text-sm font-semibold text-[#ff0000] hover:text-[#cc0000] transition-colors"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Consultar cobertura
                </a>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-[#ff0000]"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Respuesta Rápida
              </h4>
              <p className="text-sm text-gray-600">
                Desplazamiento inmediato a cualquier punto de Barcelona y provincia
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-2-line text-2xl text-[#ff0000]"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Cobertura Completa
              </h4>
              <p className="text-sm text-gray-600">
                Servicio en toda el Área Metropolitana de Barcelona (AMB)
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-car-line text-2xl text-[#ff0000]"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Equipo Móvil
              </h4>
              <p className="text-sm text-gray-600">
                Equipos profesionales listos para intervenir donde nos necesites
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              La confianza de quienes ya han trabajado con nosotros es nuestro mejor aval.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gray-50 p-8 rounded-lg shadow-md"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#ff0000] text-lg"></i>
                ))}
              </div>
              <p className="text-sm text-gray-700 mb-6 italic">
                "Después del incendio en nuestra cocina estábamos desesperados. El equipo de JUSTCLEAN llegó rápido, trabajó con mucho cuidado y dejó todo impecable. El olor a humo desapareció por completo. Muy profesionales y atentos en todo momento."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-gray-600"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">María González</p>
                  <p className="text-sm text-gray-600">Barcelona, Eixample</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-gray-50 p-8 rounded-lg shadow-md"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#ff0000] text-lg"></i>
                ))}
              </div>
              <p className="text-sm text-gray-700 mb-6 italic">
                "Tuvimos un incendio en el piso de arriba y el hollín afectó varias habitaciones. JUSTCLEAN coordinó perfectamente con el perito del seguro y nos explicaron cada paso. El resultado fue excelente, las paredes quedaron listas para pintar."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-gray-600"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Jordi Martínez</p>
                  <p className="text-sm text-gray-600">Sabadell</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gray-50 p-8 rounded-lg shadow-md"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#ff0000] text-lg"></i>
                ))}
              </div>
              <p className="text-sm text-gray-700 mb-6 italic">
                "Servicio impecable. Vinieron al día siguiente de llamarles, protegieron bien las zonas comunes del edificio y trabajaron con equipos de protección. La comunicación fue clara y el precio justo. Los recomiendo sin dudarlo."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-gray-600"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Carmen López</p>
                  <p className="text-sm text-gray-600">L'Hospitalet de Llobregat</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ¿Necesitas limpieza post incendio urgente?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Contacta con nosotros para una evaluación sin compromiso. Coordinamos con tu aseguradora y perito para agilizar el proceso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+34602365996"
              className="px-8 py-4 bg-[#ff0000] text-white text-base font-semibold rounded-lg hover:bg-[#cc0000] transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              602 36 59 96
            </a>
            <Link
              to="/contacto"
              className="px-8 py-4 bg-white text-gray-900 text-base font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              Formulario de Contacto
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <PhotoModal isOpen={isPhotoModalOpen} onClose={() => setIsPhotoModalOpen(false)} />
    </>
  );
};

export default HomePage;
